import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./NotificationOverlay.module.css";
import classNames from "classnames";
import addIcon from "../../assets/addIcon.svg";
import checkIcon from "../../assets/check.svg";

const AUTO_DISMISS_MS = 2000;
// Interaction events that should dismiss the notification early, since it
// has no interactable elements of its own.
const DISMISS_ON_EVENTS = ["click", "touchstart", "keydown", "scroll"];

/**
 * View component. A brief, non-interactive overlay that notifies the user of
 * an action (e.g. item added/deleted, address saved). Auto-dismisses after
 * ~2 seconds, or immediately on any page interaction.
 */
export function NotificationOverlay({
  overlayText,
  isVisible,
  onDismiss,
  variant,
}) {
  useEffect(() => {
    if (!isVisible) return undefined;

    const dismiss = () => onDismiss?.();
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);

    DISMISS_ON_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, dismiss, { passive: true })
    );

    return () => {
      clearTimeout(timer);
      DISMISS_ON_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, dismiss)
      );
    };
  }, [isVisible, onDismiss]);

  const icon = variant === "success" ? checkIcon : addIcon;

  return (
    <div
      className={classNames(styles.overlay, {
        [styles.invisible]: !isVisible,
      })}
      role="status"
      aria-live="polite"
    >
      <div className={styles.content}>
        <img src={icon} alt="" className={styles.addIcon} />
        <p>{overlayText}</p>
      </div>
    </div>
  );
}

NotificationOverlay.propTypes = {
  /**
   * Text of the overlay.
   */
  overlayText: PropTypes.string,
  /**
   * Determines if the overlay is visible.
   */
  isVisible: PropTypes.bool,
  /**
   * Called when the overlay should close, either after the auto-dismiss
   * timeout or on the first page interaction while visible.
   */
  onDismiss: PropTypes.func,
  /**
   * Visual variant. "success" shows a checkmark icon.
   */
  variant: PropTypes.oneOf(["default", "success"]),
};

NotificationOverlay.defaultProps = {
  variant: "default",
};

export default NotificationOverlay;
